import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../api'
import { useAuth } from '../AuthContext'

export default function useProductFavorites() {
  const { token, freshLoginRef } = useAuth()
  const tokenRef = useRef(token)
  useEffect(() => { tokenRef.current = token }, [token])

  const [saved, setSaved] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('lyannaj_product_favs') || '[]')
    } catch { return [] }
  })

  // Voir useVisitBasket : `pret` distingue « aucun favori » de « pas encore
  // charge », sans quoi l'ecran vide clignote au premier affichage (E-12).
  // On memorise le TOKEN servi (pas un booleen) pour qu'il n'existe aucun rendu
  // ou l'on serait connecte et « pret » a la fois avant la reponse du serveur.
  const [tokenServi, setTokenServi] = useState(null)
  const pret = !token || tokenServi === token

  useEffect(() => {
    let vivant = true
    if (token) {
      const isFreshLogin = freshLoginRef.current
      freshLoginRef.current = false // Reset apres que les deux hooks ont lu la valeur
      const localFavs = JSON.parse(localStorage.getItem('lyannaj_product_favs') || '[]')
      const request = (isFreshLogin && localFavs.length > 0)
        ? api.productFavorites.sync(localFavs, token)
        : api.productFavorites.get(token)
      request
        .then(dbFavs => {
          if (!vivant) return
          setSaved(dbFavs)
          localStorage.setItem('lyannaj_product_favs', JSON.stringify(dbFavs))
        })
        .catch(() => {})
        .finally(() => { if (vivant) setTokenServi(token) })
    } else {
      try {
        setSaved(JSON.parse(localStorage.getItem('lyannaj_product_favs') || '[]'))
      } catch { setSaved([]) }
      setTokenServi(null)
    }
    return () => { vivant = false }
  }, [token])

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && tokenRef.current) {
        api.productFavorites.get(tokenRef.current)
          .then(dbFavs => {
            setSaved(dbFavs)
            localStorage.setItem('lyannaj_product_favs', JSON.stringify(dbFavs))
          })
          .catch(() => {})
      }
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [])

  const toggle = useCallback((productOfferId) => {
    setSaved(prev => {
      const isRemoving = prev.includes(productOfferId)
      const next = isRemoving
        ? prev.filter(id => id !== productOfferId)
        : [...prev, productOfferId]
      localStorage.setItem('lyannaj_product_favs', JSON.stringify(next))
      const t = tokenRef.current
      if (t) {
        (isRemoving ? api.productFavorites.remove(productOfferId, t) : api.productFavorites.add(productOfferId, t)).catch(() => {})
      }
      return next
    })
  }, [])

  const isSaved = useCallback((productOfferId) => saved.includes(productOfferId), [saved])

  const clear = useCallback(() => {
    setSaved([])
    localStorage.removeItem('lyannaj_product_favs')
    const t = tokenRef.current
    if (t) {
      api.productFavorites.clearAll(t).catch(() => {})
    }
  }, [])

  return { saved, toggle, isSaved, count: saved.length, clear, pret }
}
